import React from 'react';
import styled, { css } from 'styled-components';

import Bubble from '@lt/components/components/Bubble';
import HintButton from '@lt/components/components/HintButton';

import TouristFieldInput from './TouristFieldInput';
import withMobile from '../../routes/Checkout/CheckoutRoutes/PackageCheckout/hocs/withMobile';
import type { FieldType, TouristFieldProps } from './types';

interface State {
  isValid: boolean;
  focused: boolean;
}

class TouristField extends React.PureComponent<
  Readonly<TouristFieldProps>,
  State
> {
  static getDerivedStateFromProps(props, state) {
    const { errors, stateField } = props;
    const errMsg = errors ? errors[stateField] : null;
    if (errMsg) {
      return { isValid: false };
    }
    if (state.isValid === undefined && !props.value) {
      return null;
    }
    return { isValid: true };
  }

  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      isValid: true,
    };

    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  onFocus() {
    this.setState({ focused: true });
  }

  onBlur() {
    this.setState({ focused: false });
  }

  get isValidationShown() {
    const { stateField, value } = this.props;
    const { isValid, focused } = this.state;

    // no show on empty fields, unless there's an error
    if (!stateField || (!value && isValid)) {
      return false;
    }
    return isValid !== undefined && !focused;
  }

  get fieldId() {
    const { idx, stateField } = this.props;
    return `[${idx}].${stateField}`;
  }

  render() {
    const {
      label,
      extraLabel,
      children,
      errors,
      stateField,
      isMobile,
      tooltipText,
      fieldType,
      value = '',
      className,
      disabled = false,
      ...props
    } = this.props;

    const { isValid, focused } = this.state;
    const errMsg =
      Object.values(errors).length !== 0 ? errors[stateField] : null;
    const isFirstErrorMessage = errMsg
      ? errMsg === Object.values(errors)[0]
      : false;

    const params = {
      ...props,
      errMsg,
      focused,
      stateField,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
    };

    const hasTooltip = Boolean(tooltipText);

    return (
      <StyledTouristField
        isMobile={isMobile}
        isValid={isValid}
        isValidationShown={this.isValidationShown}
        fieldType={fieldType}
        className={className}
        disabled={disabled}
      >
        <Label isMobile={isMobile} disabled={disabled}>
          {label}
          {hasTooltip ? (
            <StyledBubble text={tooltipText}>
              <StyledHintButton />
            </StyledBubble>
          ) : null}{' '}
          {extraLabel && <ExtraLabel>{` ${extraLabel}`}</ExtraLabel>}
        </Label>
        <StyledErrorBubble
          permanent={isFirstErrorMessage && !isMobile}
          type="warning"
          text={isFirstErrorMessage && !isMobile ? errMsg : ''}
        >
          <FieldContent
            isValid={isValid}
            isValidationShown={this.isValidationShown}
            fieldType={fieldType}
            isMobile={isMobile}
            id={this.fieldId}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(isMobile ? {} : { 'data-validation-error': errMsg })}
          >
            {children || <TouristFieldInput value={value} {...params} />}
          </FieldContent>
        </StyledErrorBubble>
        {isMobile && <ValidationError>{errMsg}</ValidationError>}
      </StyledTouristField>
    );
  }
}

const StyledBubble = styled(Bubble)`
  vertical-align: text-bottom;
  margin-left: 4px;
`;

const StyledErrorBubble = styled(Bubble)`
  > div {
    width: 100%;
  }
`;

const StyledHintButton = styled(HintButton)`
  width: 11px;
  height: 11px;
  font-size: 8px;
`;

export const FieldContent = styled.div<{
  isValid: boolean;
  isValidationShown: boolean;
  fieldType: FieldType;
  isMobile: boolean;
}>`
  padding-right: 5px;
  padding-left: 8px;
  display: flex;
  box-sizing: border-box;
  max-width: 100%;
  height: 46px;
  align-items: center;
  border: solid 1px #d6d5c3;
  background: #fff;
  border-right: none;
  ${({ isValid, isValidationShown }) =>
    isValidationShown &&
    `
    ${
      isValid
        ? `background: #f2fae8;`
        : `background: #fffbe6;
          border-color: #f7aa1b;
          border-right-color: #f7aa1b !important;`
    }
    `}
  ${({ fieldType }) =>
    fieldType === 'gender' &&
    `
    display: flex;
    justify-content: center;
    padding: 0px;
  `}
  ${({ fieldType, isMobile }) =>
    (fieldType === 'gender' || fieldType === 'number') &&
    isMobile &&
    `
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  `}
  ${({ fieldType, isMobile }) =>
    (fieldType === 'name' || fieldType === 'citizenship') &&
    isMobile &&
    `
    border-right: solid 1px #c2c5cc;;
  `}
`;

const ValidationError = styled.div`
  font-size: 11px;
  padding-right: 7px;
  color: #995900;
`;

export const Label = styled.div<{ isMobile: boolean; disabled: boolean }>`
  color: ${(props) => (props.disabled ? '#919499' : '#4d4d4d')};
  height: 13px;
  padding-left: 2px;
  display: flex;
  ${({ isMobile }) =>
    isMobile
      ? `
    margin-bottom: 2px;
    font-size: 9px;
    text-transform: uppercase;
  `
      : `
    margin-bottom: 4px;
    font-size: 12px;
    `}
`;

const ExtraLabel = styled.div`
  color: #919499;
  margin-left: 4px;
`;

export const StyledTouristField = styled.div<{
  isMobile: boolean;
  isValid: boolean;
  isValidationShown: boolean;
  fieldType: FieldType;
  disabled: boolean;
}>`
  display: flex;
  flex-direction: column;
  &.last-input ${FieldContent} {
    border-right: solid 1px #c2c5cc;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  ${({ fieldType }) =>
    (fieldType === 'name' || fieldType === 'patronymic') &&
    `
    min-width: 14ch;
    flex-grow: 1;
  `}
  ${({ fieldType }) =>
    fieldType === 'name' &&
    `
      &:first-of-type {
        ${FieldContent} {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }
      }
    `}
  ${({ fieldType, isMobile }) =>
    (fieldType === 'name' || fieldType === 'patronymic') &&
    isMobile &&
    `
    min-width: 14ch;
    max-width: none;
    flex-basis: 100%;

    ${FieldContent} {
      border-radius: 4px;
    }
  `}
  ${({ fieldType, isMobile }) =>
    fieldType === 'number' &&
    isMobile &&
    `
    flex-basis: 50%;
  `}
  ${({ fieldType, isMobile }) =>
    fieldType === 'date' &&
    isMobile &&
    `
    &.last-input {
      flex-basis: 50% !important;
    }
  `}
  ${({ isMobile }) =>
    isMobile
      ? `
    margin-bottom: 7px;
    min-width: 40px;
    input {
      box-sizing: border-box;
      width: 100%;
      font-size: 18px;
      font-weight: bold;
      text-overflow: ellipsis;
      color: #17181a;
      border: none;
      text-overflow: ellipsis;
      background-color: inherit;
      &::-ms-clear {
        display: none;
      }

      &:-ms-input-placeholder {
        opacity: 1;
        font-weight: normal;
        color: #c2c5cc;
      }

      &::placeholder {
        opacity: 1;
        font-weight: normal;
        color: #c2c5cc;
      }
    }
  `
      : `
    min-width: 33px;
    input {
      width: 100%;
      font-size: 14px;
      font-weight: bold;
      text-overflow: ellipsis;
      color: #17181a;
      background-color: transparent;
      border: none;

      &::-ms-clear {
        display: none;
      }

      &:-ms-input-placeholder {
        opacity: 1;
        font-weight: normal;
        color: #c2c5cc;
      }

      &::placeholder {
        opacity: 1;
        font-weight: normal;
        color: #c2c5cc;
      }
    }
  `}
  ${({ isValid }) =>
    isValid &&
    `
    &:not(.tourist-date-field):not(.tourist-citizenship) {
    ${FieldContent} {
      border-left-color: #d6d5c3;
    }
  }
  `}
  ${({ isValid, isValidationShown }) =>
    !isValid &&
    isValidationShown &&
    `
    &.last-input ${FieldContent} {
      border-right-color: #f7aa1b;
    }
  `}
  ${({ disabled }) =>
    disabled &&
    css`
      ${FieldContent} {
        background-color: transparent;

        > div {
          visibility: hidden;
        }
      }
    `}
`;

export default withMobile(TouristField);
